import classNames from 'classnames';
import React, { forwardRef } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography, {
  TypographyStyle,
} from '@/components/atoms/typography/typography';
import Pill from '@/components/molecules/pill/pill';
import styles from './checkbox.module.scss';

export interface CheckboxProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Indicates whether to center align the content with the checkbox */
  centerAlignCheckbox?: boolean;
  /** Indicates whether the field is checked */
  checked?: boolean;
  /** The number of items selected */
  count?: number;
  /** Indicates whether the field is disabled */
  disabled?: boolean;
  /** A text that is shown below the field and affects the styling */
  error?: string;
  /** If the checkbox is in the header */
  groupHeader?: boolean;
  /** The text that is shown to the right of the field */
  label?: string;
  /** An event that is triggered on change */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Indicates whether the field is partially checked - used for groups */
  partial?: boolean;
  /** A secondary text that is shown right aligned in the field */
  subText?: string;
  /** The typography styling to be used */
  tagStyle?: TypographyStyle;
  /** The value of the field */
  value?: string;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      centerAlignCheckbox = true,
      checked,
      children,
      className,
      count,
      disabled,
      error,
      groupHeader = false,
      label,
      partial,
      required,
      subText,
      tagStyle,
      ...rest
    },
    ref,
  ) => {
    return (
      <div
        className={classNames(styles.component, className, {
          [styles.hasError]: error,
          [styles.isChecked]: checked,
          [styles.isDisabled]: disabled,
          [styles.isPartial]: partial,
          [styles.isGroupHeader]: groupHeader,
        })}
      >
        <label className={classNames(styles.label)}>
          <span
            className={classNames(styles.inputWrapper, {
              [styles.isCenterAligned]: centerAlignCheckbox,
            })}
          >
            <input
              {...rest}
              ref={ref}
              checked={checked}
              className={styles.input}
              disabled={disabled}
              type="checkbox"
            />

            <span className={styles.field}>
              {checked ? (
                <Icon className={styles.icon} name="CheckMark" size={12} />
              ) : (
                partial && (
                  <Icon className={styles.icon} name="Minus" size={12} />
                )
              )}
            </span>

            <div className={styles.labelWrapper}>
              <Typography tagStyle={tagStyle}>
                {label}

                {label && required && !children && (
                  <span className={styles.requiredMark}> *</span>
                )}

                {children}

                {children && required && !label && (
                  <span className={styles.requiredMark}> *</span>
                )}
              </Typography>

              {error && (
                <Typography className={styles.error} tagStyle="bodySmall">
                  {error}
                </Typography>
              )}
            </div>
          </span>

          {subText && (
            <Typography
              className={styles.subText}
              tag="span"
              tagStyle="bodySmall"
            >
              {subText}
            </Typography>
          )}

          {typeof count === 'number' && <Pill count={count} />}
        </label>
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
